import * as THREE from 'three';

export default class DryBush {
    static windTime = 0;
    static windStrength = 0.2;
    static windFrequency = 1.5;
    constructor(scene, physics, position = { x: 0, z: 0 }, sizeFactor = 1) {
        this.scene = scene;
        this.physics = physics;
        this.position = position;
        this.sizeFactor = sizeFactor;
        
        // Bush properties
        this.size = 0;
        this.mesh = null;
        this.bushGroup = new THREE.Group();
        this.originalVertices = [];
        
        // Create the bush
        this.create();
    }
    
    create() {
        // Base size with variation
        this.size = (0.5 + Math.random() * 0.8) * this.sizeFactor;
        
        // Create bush material with dry, weathered appearance
        const hue = 0.08 + (Math.random() * 0.05); // Brownish-yellow hue
        const saturation = 0.3 + (Math.random() * 0.2);
        const lightness = 0.2 + (Math.random() * 0.15);
        const color = new THREE.Color().setHSL(hue, saturation, lightness);
        
        const bushMaterial = new THREE.MeshStandardMaterial({
            color: color,
            roughness: 0.9,
            metalness: 0.1,
            flatShading: true
        });
        
        // Create multiple small, irregular shapes for the bush
        const numSegments = 3 + Math.floor(Math.random() * 3);
        
        for (let i = 0; i < numSegments; i++) {
            const segmentSize = this.size * (0.6 + Math.random() * 0.4);
            const geometry = new THREE.DodecahedronGeometry(segmentSize, 0);
            
            // Distort the geometry for a more natural look
            const vertices = geometry.attributes.position;
            for (let j = 0; j < vertices.count; j++) {
                const x = vertices.getX(j);
                const y = vertices.getY(j);
                const z = vertices.getZ(j);
                
                vertices.setXYZ(j,
                    x + (Math.random() - 0.5) * segmentSize * 0.3,
                    y + (Math.random() - 0.5) * segmentSize * 0.2,
                    z + (Math.random() - 0.5) * segmentSize * 0.3
                );
            }
            vertices.needsUpdate = true;
            
            // Store original vertices for wind animation
            const originalPositions = new Float32Array(vertices.array);
            this.originalVertices.push(originalPositions);
            
            const segment = new THREE.Mesh(geometry, bushMaterial);
            
            // Position segment within the bush group
            segment.position.set(
                (Math.random() - 0.5) * this.size * 0.8,
                segmentSize * 0.5 + (Math.random() * this.size * 0.3),
                (Math.random() - 0.5) * this.size * 0.8
            );
            
            // Random rotation
            segment.rotation.x = Math.random() * Math.PI;
            segment.rotation.y = Math.random() * Math.PI;
            segment.rotation.z = Math.random() * Math.PI;
            
            // Random scale
            const scale = 0.7 + Math.random() * 0.6;
            segment.scale.set(scale, scale * 1.2, scale);
            
            segment.castShadow = true;
            segment.receiveShadow = true;
            
            this.bushGroup.add(segment);
        }
        
        // Position the entire bush group at terrain height
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(this.position.x, this.position.z) : 0;
        this.bushGroup.position.set(
            this.position.x,
            terrainY,
            this.position.z
        );
        
        this.scene.add(this.bushGroup);
    }
    
    // For removing the bush from the scene if needed
    remove() {
        if (this.bushGroup) {
            this.scene.remove(this.bushGroup);
        }
    }

    // Update wind effect
    update(deltaTime) {
        DryBush.windTime += deltaTime;
        
        this.bushGroup.children.forEach((segment, index) => {
            const vertices = segment.geometry.attributes.position;
            const phase = index * 0.2; // Different phase for each segment
            
            for (let i = 0; i < vertices.count; i++) {
                const originalX = this.originalVertices[index][i * 3];
                const originalY = this.originalVertices[index][i * 3 + 1];
                const originalZ = this.originalVertices[index][i * 3 + 2];
                
                // Apply wind effect
                const windEffect = Math.sin(DryBush.windTime * DryBush.windFrequency + phase) * DryBush.windStrength;
                vertices.setX(i, originalX + windEffect * (originalY / this.size));
                vertices.setZ(i, originalZ + windEffect * (originalY / this.size));
            }
            
            vertices.needsUpdate = true;
        });
    }
}